<app-configuration-page
  title="Studio Information"
  description="Keep studio identity, hours, and AMI reporting details in sync for your team."
  [breadcrumbs]="[{ label: 'Studio Information' }]">

  <section class="studio-overview mt-3">
    <mat-card class="overview-card">
        <mat-card-header>
            <div mat-card-avatar class="overview-icon">
                <mat-icon>location_city</mat-icon>
            </div>
            <mat-card-title>Studio at a glance</mat-card-title>
            <mat-card-subtitle>Core checkpoints for your primary location</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <div class="overview-content">
                <div class="overview-stat">
                    <span class="label">Profile</span>
                    <mat-chip *ngIf="isOverviewLoading" class="overview-chip-loading" selected>
                        <mat-spinner diameter="14" class="me-2"></mat-spinner>
                        Checking&hellip;
                    </mat-chip>
                    <mat-chip *ngIf="!isOverviewLoading" [color]="areOperationsConfigured ? 'primary' : 'warn'" selected>
                        {{ areOperationsConfigured ? 'Complete' : 'Needs attention' }}
                    </mat-chip>
                    <p>Review contact data monthly to keep directories current.</p>
                </div>
                <div class="overview-stat">
                    <span class="label">Operations</span>
                    <ul class="list-unstyled mb-0 operations-list">
                        <li *ngFor="let status of operationStatuses">
                            <mat-icon [ngClass]="{
                                'status-loading': status.loading,
                                'status-complete': !status.loading && status.configured,
                                'status-missing': !status.loading && !status.configured
                            }">
                                {{ status.loading ? 'autorenew' : (status.configured ? 'check_circle' : 'radio_button_unchecked') }}
                            </mat-icon>
                            <span>{{ status.label }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </mat-card-content>
    </mat-card>
  </section>

  <section class="card-grid mt-4">
  <mat-card class="settings-card">
    <mat-card-header class="mb-3">
        <div mat-card-avatar class="card-icon">
            <mat-icon>badge</mat-icon>
        </div>
        <mat-card-title>Studio Profile</mat-card-title>
        <mat-card-subtitle>Core studio identity and contact details</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
        <p class="mb-2">Configure your studio's basic information visible throughout the system.</p>
        <ul class="mb-0">
            <li>Studio name and display settings</li>
            <li>Contact phone, email, and website</li>
            <li>Physical address and timezone</li>
        </ul>
    </mat-card-content>
    <mat-card-actions align="end" class="pt-3">
        <button mat-flat-button color="primary" type="button" (click)="openStudioInformationDialog()">Edit Studio Details</button>
    </mat-card-actions>
  </mat-card>

  <mat-card class="settings-card">
        <mat-card-header class="mb-3">
            <div mat-card-avatar class="card-icon">
                <mat-icon>calendar_month</mat-icon>
            </div>
            <mat-card-title>Working Hours &amp; Session Times</mat-card-title>
            <mat-card-subtitle>Now under Calendar Configuration</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <p class="mb-2">Studio working hours and lesson session start times have moved to the Calendar Configuration page, alongside the other calendar settings.</p>
        </mat-card-content>
        <mat-card-actions align="end" class="pt-3">
            <button mat-flat-button color="primary" type="button" routerLink="/configuration/calendar-configuration" [queryParams]="{ tab: 'working-hours' }">Go to Calendar Configuration</button>
        </mat-card-actions>
    </mat-card>

  <mat-card class="settings-card">
    <mat-card-header class="mb-3">
        <div mat-card-avatar class="card-icon">
            <mat-icon>insights</mat-icon>
        </div>
        <mat-card-title>AMI Reporting</mat-card-title>
        <mat-card-subtitle>Arthur Murray International integration</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
        <p class="mb-2">Connect to AMI's reporting system for franchise compliance.</p>
        <ul class="mb-0">
            <li>API credentials and endpoints</li>
            <li>Live or testing environment</li>
            <li>Submit reports easily with just a click</li>
        </ul>
    </mat-card-content>
    <mat-card-actions align="end" class="pt-3">
        <ng-container *ngIf="canConfigureReporting; else adminHandled">
            <button mat-flat-button color="primary" type="button" (click)="openAmiReportingDialog()">Configure Reporting</button>
        </ng-container>
        <ng-template #adminHandled>
            <button mat-flat-button color="primary" type="button" disabled>
                Admin Only
            </button>
        </ng-template>
    </mat-card-actions>
  </mat-card>
  <mat-card class="settings-card">
    <mat-card-header class="mb-3">
        <div mat-card-avatar class="card-icon">
            <mat-icon>description</mat-icon>
        </div>
        <mat-card-title>Enrollment Policies</mat-card-title>
        <mat-card-subtitle>Agreement & lesson policy settings</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
        <p class="mb-2">Configure enrollment agreement templates and lesson balance policies.</p>
        <ul class="mb-0">
            <li>Select active enrollment agreement</li>
            <li>Control negative balance rules</li>
            <li>Preview customer-facing forms</li>
        </ul>
    </mat-card-content>
    <mat-card-actions align="end" class="pt-3">
        <button mat-flat-button color="primary" type="button" (click)="openEnrollmentPoliciesDialog()">Edit Policies</button>
    </mat-card-actions>
  </mat-card>
  </section>

  <section class="support-panel mt-4 d-none">
    <mat-card class="support-card">
        <mat-card-content>
            <div class="support-content">
                <div>
                    <h3>Need help refreshing studio settings?</h3>
                    <p class="text-muted mb-0">Most changes take effect instantly. For anything urgent, reach out to support so we can monitor downstream updates.</p>
                </div>
                    <div class="support-actions">
                        <button mat-flat-button color="primary" class="support-btn" type="button">View documentation</button>
                        <button mat-flat-button color="primary" class="support-btn" type="button">Contact support</button>
                    </div>
            </div>
        </mat-card-content>
    </mat-card>
  </section>

</app-configuration-page>
